import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftAddon,
  Textarea,
  Box,
  Text,
  Flex,
  Divider,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdCreditCard,
  MdAccountBalanceWallet,
  MdSwapHoriz,
  MdCheckCircle,
} from 'react-icons/md';
import { useRecordPayment } from 'hooks/useBillingAccount';
import { PaymentMethodType, RecordPaymentDto } from 'types/models';

interface Props {
  isOpen:       boolean;
  onClose:      () => void;
  patientId:    string;
  patientName:  string;
  currentDebt:  number;
}

const METHODS: { value: PaymentMethodType; label: string; icon: React.ElementType }[] = [
  { value: PaymentMethodType.EFECTIVO,      label: 'Efectivo',      icon: MdAccountBalanceWallet },
  { value: PaymentMethodType.TARJETA,       label: 'Tarjeta',       icon: MdCreditCard           },
  { value: PaymentMethodType.TRANSFERENCIA, label: 'Transferencia', icon: MdSwapHoriz            },
];

const NEEDS_REFERENCE = [PaymentMethodType.TARJETA, PaymentMethodType.TRANSFERENCIA];

export default function RecordPaymentModal({
  isOpen, onClose, patientId, patientName, currentDebt,
}: Props) {
  const { mutate: recordPayment, isPending } = useRecordPayment();

  const [amount,      setAmount]      = useState('');
  const [method,      setMethod]      = useState<PaymentMethodType>(PaymentMethodType.EFECTIVO);
  const [reference,   setReference]   = useState('');
  const [errors,      setErrors]      = useState<Record<string, string>>({});

  const amountNum    = amount !== '' ? parseFloat(amount) : 0;
  const debtAfter    = Math.max(0, currentDebt - amountNum);
  const isFullPayment = amountNum === currentDebt;
  const needsRef     = NEEDS_REFERENCE.includes(method);

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!amount || amountNum <= 0)             errs.amount = 'Ingrese un monto mayor a $0';
    if (amountNum > currentDebt)               errs.amount = `No puede superar la deuda actual ($${currentDebt.toFixed(2)})`;
    if (needsRef && !reference.trim())         errs.reference = 'El número de referencia es obligatorio para este método';
    if (needsRef && reference.trim().length > 100) errs.reference = 'Máximo 100 caracteres';
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    const payload: RecordPaymentDto = {
      amount: amountNum,
      paymentMethod: method,
      ...(needsRef && reference.trim() ? { referenceNumber: reference.trim() } : {}),
    };

    recordPayment({ patientId, payload }, { onSuccess: onClose });
  };

  const handleFullPayment = () => {
    setAmount(currentDebt.toFixed(2));
    setErrors(p => ({ ...p, amount: '' }));
  };

  // ── Colors ───────────────────────────────────────────────────────────────
  const borderClr    = useColorModeValue('gray.200',  'gray.600');
  const labelColor   = useColorModeValue('gray.600',  'gray.400');
  const mutedColor   = useColorModeValue('gray.500',  'gray.400');
  const readBg       = useColorModeValue('gray.50',   'navy.750');
  const methodBg     = useColorModeValue('white',     'gray.700');
  const methodActive = useColorModeValue('brand.500', 'brand.300');
  const previewBg    = useColorModeValue(debtAfter === 0 ? 'green.50' : 'orange.50', debtAfter === 0 ? 'rgba(72,187,120,0.12)' : 'rgba(251,140,0,0.10)');
  const previewBdr   = useColorModeValue(debtAfter === 0 ? 'green.200' : 'orange.200', debtAfter === 0 ? 'green.700' : 'orange.700');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="7px">

        <ModalHeader pb="4px">
          <HStack spacing="10px">
            <Flex
              w="36px" h="36px" borderRadius="9px"
              bg="brand.500" align="center" justify="center" flexShrink={0}
            >
              <Icon as={MdCreditCard} color="white" w="18px" h="18px" />
            </Flex>
            <Box>
              <Text fontSize="md" fontWeight="800">Registrar Pago</Text>
              <Text fontSize="xs" color={mutedColor} fontWeight="400">{patientName}</Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing="16px" align="stretch">

            {/* Deuda actual */}
            <Box bg={readBg} border="1px solid" borderColor={borderClr} borderRadius="10px" p="14px">
              <HStack justify="space-between">
                <Text fontSize="xs" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.06em">
                  Deuda actual
                </Text>
                <Text fontSize="xl" fontWeight="800" color="red.500">
                  ${currentDebt.toFixed(2)}
                </Text>
              </HStack>
            </Box>

            {/* Monto */}
            <FormControl isRequired isInvalid={!!errors.amount}>
              <HStack justify="space-between" mb="6px">
                <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em" mb="0">
                  Monto a pagar
                </FormLabel>
                <Button
                  size="xs" variant="ghost" colorScheme="brand"
                  fontWeight="700" h="auto" py="1px"
                  onClick={handleFullPayment}
                >
                  Pago total
                </Button>
              </HStack>
              <InputGroup size="sm">
                <InputLeftAddon borderLeftRadius="8px" fontSize="sm">$</InputLeftAddon>
                <Input
                  type="number"
                  min={0.01}
                  max={currentDebt}
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={e => { setAmount(e.target.value); setErrors(p => ({ ...p, amount: '' })); }}
                  borderRightRadius="8px"
                  fontSize="sm"
                  fontWeight="600"
                />
              </InputGroup>
              <FormErrorMessage fontSize="xs">{errors.amount}</FormErrorMessage>
            </FormControl>

            {/* Método de pago */}
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
                Método de pago
              </FormLabel>
              <HStack spacing="8px">
                {METHODS.map(m => {
                  const active = method === m.value;
                  return (
                    <Flex
                      key={m.value}
                      flex="1"
                      direction="column"
                      align="center"
                      gap="4px"
                      py="10px"
                      px="8px"
                      bg={methodBg}
                      border="2px solid"
                      borderColor={active ? methodActive : borderClr}
                      borderRadius="10px"
                      cursor="pointer"
                      transition="all 0.15s"
                      onClick={() => { setMethod(m.value); setReference(''); setErrors(p => ({ ...p, reference: '' })); }}
                      _hover={{ borderColor: active ? methodActive : 'brand.200' }}
                    >
                      <Icon
                        as={m.icon}
                        w="20px" h="20px"
                        color={active ? methodActive : mutedColor}
                        transition="color 0.15s"
                      />
                      <Text
                        fontSize="xs" fontWeight={active ? '700' : '500'}
                        color={active ? methodActive : mutedColor}
                        transition="all 0.15s"
                      >
                        {m.label}
                      </Text>
                    </Flex>
                  );
                })}
              </HStack>
            </FormControl>

            {/* Referencia — solo Tarjeta / Transferencia */}
            {needsRef && (
              <FormControl isRequired isInvalid={!!errors.reference}>
                <FormLabel fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="0.06em">
                  N.° de referencia / comprobante
                </FormLabel>
                <Input
                  size="sm"
                  placeholder={method === PaymentMethodType.TARJETA ? 'Últimos 4 dígitos o auth. code…' : 'N.° de transferencia / comprobante…'}
                  value={reference}
                  onChange={e => { setReference(e.target.value); setErrors(p => ({ ...p, reference: '' })); }}
                  maxLength={100}
                  fontSize="sm"
                  borderRadius="8px"
                />
                <FormErrorMessage fontSize="xs">{errors.reference}</FormErrorMessage>
              </FormControl>
            )}

            {/* Preview deuda posterior */}
            {amountNum > 0 && !errors.amount && (
              <Box
                bg={previewBg}
                border="1px solid"
                borderColor={previewBdr}
                borderRadius="10px"
                p="14px"
              >
                <Text
                  fontSize="xs" fontWeight="700"
                  color={debtAfter === 0 ? 'green.600' : 'orange.600'}
                  textTransform="uppercase" letterSpacing="0.06em" mb="10px"
                >
                  {debtAfter === 0 ? 'Cuenta al día' : 'Vista previa'}
                </Text>
                <VStack spacing="6px" align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color={mutedColor}>Deuda actual</Text>
                    <Text fontSize="sm" fontWeight="600">${currentDebt.toFixed(2)}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="green.600">Abono</Text>
                    <Text fontSize="sm" fontWeight="600" color="green.600">- ${amountNum.toFixed(2)}</Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="700">Deuda después</Text>
                    <HStack spacing="6px">
                      {debtAfter === 0 && <Icon as={MdCheckCircle} color="green.500" w="14px" h="14px" />}
                      <Text
                        fontSize="md" fontWeight="800"
                        color={debtAfter === 0 ? 'green.500' : 'orange.500'}
                      >
                        ${debtAfter.toFixed(2)}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              </Box>
            )}

          </VStack>
        </ModalBody>

        <ModalFooter gap="8px">
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isPending}>
            Cancelar
          </Button>
          <Button
            colorScheme="brand"
            size="sm"
            borderRadius="8px"
            onClick={handleSubmit}
            isLoading={isPending}
            loadingText="Procesando…"
            isDisabled={!amount || amountNum <= 0}
          >
            Confirmar pago
          </Button>
        </ModalFooter>

      </ModalContent>
    </Modal>
  );
}
